import { AlertTriangle, X } from 'lucide-react';
import { Dialog as RD } from 'radix-ui';
import { useState, type ReactElement, type ReactNode } from 'react';
import { cx } from '../lib/cx.js';
import { Button } from './Button.js';
import './dialog.css';

export interface DialogProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Optional trigger element; omit for programmatic dialogs. */
  trigger?: ReactElement;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  /** Footer actions, right-aligned. */
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  /** Hide the close button (for flows that must end with a choice). */
  hideClose?: boolean;
  className?: string;
}

/** Modal dialog. For work done while looking at the schedule or chart, prefer a Drawer. */
export function Dialog({
  open,
  onOpenChange,
  trigger,
  title,
  description,
  children,
  footer,
  size = 'md',
  hideClose = false,
  className,
}: DialogProps) {
  return (
    <RD.Root {...(open !== undefined ? { open } : {})} {...(onOpenChange ? { onOpenChange } : {})}>
      {trigger ? <RD.Trigger asChild>{trigger}</RD.Trigger> : null}
      <RD.Portal>
        <RD.Overlay className="vel-overlay" />
        <RD.Content
          className={cx('vel-dialog', className)}
          data-size={size}
          {...(description ? {} : { 'aria-describedby': undefined })}
        >
          <header className="vel-dialog__header">
            <RD.Title className="vel-dialog__title">{title}</RD.Title>
            {description ? (
              <RD.Description className="vel-dialog__desc">{description}</RD.Description>
            ) : null}
            {hideClose ? null : (
              <RD.Close className="vel-dialog__close" aria-label="Close">
                <X size={16} aria-hidden />
              </RD.Close>
            )}
          </header>
          {children ? <div className="vel-dialog__body">{children}</div> : null}
          {footer ? <footer className="vel-dialog__footer">{footer}</footer> : null}
        </RD.Content>
      </RD.Portal>
    </RD.Root>
  );
}

export const DialogClose = RD.Close;

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  /**
   * Name the subject and the effect (BUILD-PLAN 3.3): "Delete Ana Ruiz's
   * 9:00 appointment? Her recall attachment is also removed."
   */
  description: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  destructive?: boolean;
  /** May return a promise; the dialog shows progress and stays open on rejection. */
  onConfirm: () => void | Promise<void>;
  /** Extra content, e.g. a reason field required before confirming. */
  children?: ReactNode;
  confirmDisabled?: boolean;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  cancelLabel = 'Cancel',
  destructive = false,
  onConfirm,
  children,
  confirmDisabled,
}: ConfirmDialogProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function run() {
    setBusy(true);
    setError(null);
    try {
      await onConfirm();
      onOpenChange(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That did not work. Try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <RD.Root
      open={open}
      onOpenChange={(o) => {
        if (!busy) {
          setError(null);
          onOpenChange(o);
        }
      }}
    >
      <RD.Portal>
        <RD.Overlay className="vel-overlay" />
        <RD.Content className="vel-dialog" data-size="sm" role="alertdialog">
          <header className="vel-dialog__header">
            <RD.Title className="vel-dialog__title">
              {destructive ? (
                <span className="vel-dialog__danger-icon" aria-hidden>
                  <AlertTriangle size={16} />
                </span>
              ) : null}
              {title}
            </RD.Title>
            <RD.Description className="vel-dialog__desc">{description}</RD.Description>
          </header>
          {children ? <div className="vel-dialog__body">{children}</div> : null}
          {error ? (
            <p className="vel-dialog__error" role="alert">
              {error}
            </p>
          ) : null}
          <footer className="vel-dialog__footer">
            <RD.Close asChild>
              <Button variant="ghost" disabled={busy}>
                {cancelLabel}
              </Button>
            </RD.Close>
            <Button
              variant={destructive ? 'danger' : 'primary'}
              loading={busy}
              disabled={confirmDisabled}
              onClick={() => void run()}
            >
              {confirmLabel}
            </Button>
          </footer>
        </RD.Content>
      </RD.Portal>
    </RD.Root>
  );
}
